/**
 * useMicCapture — record a short mic clip via `getUserMedia` +
 * `MediaRecorder`. Ported (cleanly reimplemented, not copied) from the
 * `toggleMic()` pattern in H:\dev\wayfinder\web\app.js: request the mic,
 * record into a `MediaRecorder`, auto-stop after `maxMs` (default 6s),
 * hand back the encoded blob bytes + mime type for the caller to feed
 * into ASR.
 *
 * Feature-detects the best-supported mime type via
 * `MediaRecorder.isTypeSupported` (Safari doesn't support `audio/webm`;
 * falls back to whatever the browser offers) instead of hardcoding
 * `audio/webm`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
const CANDIDATE_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
function pickMimeType() {
    if (typeof MediaRecorder === 'undefined')
        return 'audio/webm';
    for (const candidate of CANDIDATE_MIME_TYPES) {
        if (MediaRecorder.isTypeSupported(candidate))
            return candidate;
    }
    return '';
}
export function useMicCapture(opts = {}) {
    const maxMs = opts.maxMs ?? 6000;
    const [recording, setRecording] = useState(false);
    const [error, setError] = useState(null);
    const [lastClip, setLastClip] = useState(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const cleanupStream = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        recorderRef.current = null;
    }, []);
    const stop = useCallback(() => {
        const recorder = recorderRef.current;
        if (recorder && recorder.state !== 'inactive') {
            recorder.stop();
        }
        else {
            cleanupStream();
            setRecording(false);
        }
    }, [cleanupStream]);
    const start = useCallback(() => {
        if (recording)
            return;
        setError(null);
        void (async () => {
            try {
                console.debug('[legion-speech] mic: requesting getUserMedia({audio:true})…');
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                streamRef.current = stream;
                const mimeType = pickMimeType();
                console.debug(`[legion-speech] mic: stream granted, recording (mime=${mimeType || 'default'}, maxMs=${maxMs})`);
                const recorder = mimeType
                    ? new MediaRecorder(stream, { mimeType })
                    : new MediaRecorder(stream);
                recorderRef.current = recorder;
                chunksRef.current = [];
                recorder.ondataavailable = (ev) => {
                    if (ev.data.size > 0)
                        chunksRef.current.push(ev.data);
                };
                recorder.onstop = () => {
                    void (async () => {
                        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mimeType || 'audio/webm' });
                        const bytes = await blob.arrayBuffer();
                        console.debug(`[legion-speech] mic: stopped — clip ${bytes.byteLength} bytes (${blob.type || mimeType || 'audio/webm'})`);
                        setLastClip({ bytes, mimeType: blob.type || mimeType || 'audio/webm' });
                        cleanupStream();
                        setRecording(false);
                    })();
                };
                recorder.start();
                setRecording(true);
                timerRef.current = setTimeout(() => stop(), maxMs);
            }
            catch (err) {
                console.error('[legion-speech] mic: getUserMedia/record failed', err);
                setError(err instanceof Error ? err.message : String(err));
                cleanupStream();
                setRecording(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recording, maxMs, cleanupStream]);
    useEffect(() => {
        return () => {
            const recorder = recorderRef.current;
            if (recorder && recorder.state !== 'inactive') {
                recorder.stop();
            }
            cleanupStream();
        };
    }, [cleanupStream]);
    return { recording, error, start, stop, lastClip };
}
